import React, { useMemo, useState } from "react";
import { Tabs, Table, Input, Empty, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const NAVY = "#0B1F3A";

// Column Level columns whose value is a Y/N flag → render as a coloured tag.
const YN_COLUMNS = new Set([
  "Mandatory Column",
  "Not Null Check",
  "Allowable Values",
  "Row Key Field",
]);

function ynTag(v) {
  const val = String(v || "").trim().toUpperCase();
  if (val === "Y" || val === "YES")
    return <Tag color="green" style={{ margin: 0 }}>Y</Tag>;
  if (val === "N" || val === "NO")
    return <Tag style={{ margin: 0 }}>N</Tag>;
  return val ? <span>{val}</span> : <span style={{ color: "#bbb" }}>—</span>;
}

// ─── File Level: vertical setting / value / requirement ───────────
function FileLevelTab({ rows }) {
  if (!rows?.length) return <Empty description="No File Level data" />;
  return (
    <div style={{ border: "1px solid #EEF0F3", borderRadius: 8, overflow: "hidden" }}>
      {rows.map((r, i) => (
        <div
          key={r._key}
          style={{
            display: "grid",
            gridTemplateColumns: "260px 1fr 130px",
            gap: 12,
            alignItems: "center",
            padding: "9px 14px",
            borderBottom: i < rows.length - 1 ? "1px solid #EEF0F3" : "none",
            background: i % 2 === 0 ? "#fff" : "#FAFBFC",
          }}
        >
          <span style={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>
            {r.setting}
          </span>
          <span style={{ fontSize: 13, color: "#111827", fontWeight: 600, wordBreak: "break-word" }}>
            {r.value !== "" ? r.value : <span style={{ color: "#bbb", fontWeight: 400 }}>—</span>}
          </span>
          <span>
            {r.requirement ? (
              <Tag
                color={/mandatory/i.test(r.requirement) ? "red" : "default"}
                style={{ margin: 0 }}
              >
                {r.requirement}
              </Tag>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Grid tab (Column Level / Allowable Values) ───────────────────
function GridTab({ grid }) {
  const [q, setQ] = useState("");

  const columns = useMemo(
    () =>
      (grid?.columns || []).map((c) => ({
        title: c,
        dataIndex: c,
        key: c,
        ellipsis: true,
        render: (text) =>
          YN_COLUMNS.has(c)
            ? ynTag(text)
            : c === "Column Name"
              ? <span style={{ color: NAVY, fontWeight: 600, letterSpacing: 0.2 }}>{text || "—"}</span>
              : text !== "" ? text : <span style={{ color: "#bbb" }}>—</span>,
      })),
    [grid],
  );

  const data = useMemo(() => {
    const rows = grid?.rows || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      (grid.columns || []).some((c) =>
        String(row[c] || "").toLowerCase().includes(needle),
      ),
    );
  }, [grid, q]);

  if (!grid?.columns?.length) return <Empty description="No data" />;

  return (
    <div>
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: "#9CA3AF" }} />}
        placeholder="Search..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ maxWidth: 280, marginBottom: 10 }}
      />
      <Table
        size="small"
        bordered
        columns={columns}
        dataSource={data}
        rowKey="_key"
        pagination={data.length > 25 ? { pageSize: 25, showSizeChanger: false } : false}
        scroll={{ x: "max-content", y: 380 }}
      />
    </div>
  );
}

// ─── The three-tab view, shared by review + viewer ────────────────
function SpecTabsView({ fileLevel, columnLevel, allowableValues }) {
  const items = [
    {
      key: "file",
      label: "File Level",
      children: <FileLevelTab rows={fileLevel} />,
    },
    {
      key: "column",
      label: `Column Level${columnLevel?.rows?.length ? ` (${columnLevel.rows.length})` : ""}`,
      children: <GridTab grid={columnLevel} />,
    },
    {
      key: "allowable",
      label: `Allowable Values${allowableValues?.rows?.length ? ` (${allowableValues.rows.length})` : ""}`,
      children: <GridTab grid={allowableValues} />,
    },
  ];

  return <Tabs type="card" size="middle" items={items} style={{ minHeight: 420 }} />;
}

export default SpecTabsView;
